import React, { useEffect, useState } from "react";

import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
} from "lucide-react";

import type {
  PlanFile,
  PlanProgressStep,
  PlanProgressStepName,
} from "../../Types/AccountPlan";
import { laneLine, roughRange } from "../../Utils/AccountPlans/queueText";

interface Props {
  file: PlanFile;
  /** Steps only, no heading (inside the upload dialog, which has its own). */
  compact?: boolean;
}

type RowStatus = "done" | "active" | "pending" | "failed";

/** One line under a step: a check and how it came out. */
export interface SubItem {
  label: string;
  status: "done" | "active" | "warn" | "failed";
  note?: string;
}

interface Row {
  key: string;
  label: string;
  status: RowStatus;
  ms?: number;
  startedAt?: string;
  /** What the step is doing or did, line by line (file checks, plan checks). */
  items?: SubItem[];
}

// What the server really does, in order. Slide images and the AI read run
// side by side, so both can be in progress at once.
const STEPS: { key: PlanProgressStepName; label: string }[] = [
  { key: "extract", label: "Extracting text and tables" },
  { key: "images", label: "Rendering slide images for the viewer" },
  {
    key: "read",
    label: "Reading the slides with AI — summaries and key points",
  },
  { key: "save", label: "Saving the plan" },
  { key: "check", label: "Checking the plan" },
];

/** What "Checking the plan" looks at, while it runs (validation.py, A–D). */
export const PLAN_CHECKS_RUNNING = [
  "Whose plan it is: this account, not another customer or your own company",
  "Reads like an account plan, with its main sections",
  "Template filled in, not placeholder text",
  "Text readable: not blank, garbled or pictures of text",
  "Up to date: recent years and the renewal date",
];

/** A passed check's line. A1's title names the problem ("Names a different
 * account"), which reads as a failure next to a green tick. */
const PASS_LABEL: Record<string, string> = {
  A1: "Not another account's plan",
};
export const passLabel = (c: { id: string; title: string }): string =>
  PASS_LABEL[c.id] || c.title;

/** The plan checks as they came out, one line each (file.validation). Checks
 * turned off in this environment are left out. */
export const planCheckItems = (file: PlanFile): SubItem[] | undefined => {
  const checks = file.validation?.checks;
  if (!checks?.length) return undefined;
  return checks
    .filter(c => !/Turned off in this environment/.test(c.detail || ""))
    .map(c => ({
      label: c.status === "pass" ? passLabel(c) : c.problem || c.title,
      status:
        c.status === "pass" ? "done" : c.status === "warn" ? "warn" : "failed",
      note: c.status === "pass" ? undefined : c.detail || undefined,
    }));
};

const seconds = (ms: number) =>
  ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;

const rowStatus = (s?: PlanProgressStep): RowStatus =>
  s?.state === "done"
    ? "done"
    : s?.state === "active"
      ? "active"
      : s?.state === "failed"
        ? "failed"
        : "pending";

/** Rows for the checklist, from the server's per-step progress. */
export const progressRows = (file: PlanFile): Row[] => {
  const steps = file.progress?.steps || {};
  // The file checks are made when the upload arrives; the read itself starts later.
  const started = Object.keys(steps).some(k => k !== "file");
  const rows: Row[] = [
    { key: "upload", label: "Uploaded", status: "done" },
    ...(steps.file
      ? [
          {
            key: "file",
            label: "Checked the file",
            // One line (upload-screen review, 2 Nov): the file checks' detail isn't shown.
            status: rowStatus(steps.file),
          },
        ]
      : []),
    // Accepted but not picked up yet: say so instead of showing nothing moving.
    ...(!started
      ? [
          {
            key: "queue",
            // Place in line and when it starts, once the server sends it (QUE-03).
            label: file.queue?.read
              ? laneLine(file.queue.read, "read")
              : "Waiting to start",
            status: "active" as RowStatus,
            startedAt: file.created_at || undefined,
          },
        ]
      : []),
  ];
  STEPS.forEach(({ key, label }) => {
    const s = steps[key];
    if (s?.state === "skipped") return;
    const status = rowStatus(s);
    rows.push({
      key,
      label,
      status,
      ms: s?.ms,
      startedAt: s?.started_at,
      items:
        key === "check"
          ? status === "active"
            ? PLAN_CHECKS_RUNNING.map(l => ({ label: l, status: "active" }))
            : status === "done" || status === "failed"
              ? planCheckItems(file)
              : undefined
          : undefined,
    });
  });
  return rows;
};

/** The lines under a step: each check and its result. */
export const StepItems: React.FC<{ rowKey: string; items: SubItem[] }> = ({
  rowKey,
  items,
}) => (
  <ul
    className="ml-6 mt-1 w-full space-y-1 border-l border-gray-100 pl-3 text-xs"
    aria-label={`${rowKey} details`}
    data-testid={`progress-${rowKey}-items`}
  >
    {items.map(i => (
      <li
        key={i.label}
        className="flex items-start gap-1.5"
        data-status={i.status}
      >
        {i.status === "done" ? (
          <CheckCircle2
            className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600"
            aria-label="passed"
          />
        ) : i.status === "active" ? (
          <Loader2
            className="mt-px h-3.5 w-3.5 shrink-0 animate-spin text-[#974ae4]"
            aria-label="checking"
          />
        ) : i.status === "warn" ? (
          <AlertTriangle
            className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600"
            aria-label="needs a look"
          />
        ) : (
          <XCircle
            className="mt-px h-3.5 w-3.5 shrink-0 text-red-500"
            aria-label="failed"
          />
        )}
        <span
          className={
            i.status === "active"
              ? "text-purple-800"
              : i.status === "warn"
                ? "text-amber-800"
                : i.status === "failed"
                  ? "text-red-700"
                  : "text-gray-600"
          }
        >
          {i.label}
          {i.note && <span className="block text-gray-500">{i.note}</span>}
        </span>
      </li>
    ))}
  </ul>
);

/**
 * Live checklist while a plan version is being read. The page polls the
 * overview while anything is processing; each poll brings the server's
 * current step, and the running step's clock ticks locally in between.
 */
const UploadProgress: React.FC<Props> = ({ file, compact = false }) => {
  const rows = progressRows(file);
  const anyActive = rows.some(r => r.status === "active");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [anyActive]);

  const elapsed = (startedAt?: string) => {
    const t = startedAt ? Date.parse(startedAt) : NaN;
    return Number.isNaN(t) ? null : Math.max(0, now - t);
  };

  return (
    <div
      role="status"
      className={
        compact
          ? "text-sm text-gray-700"
          : "mb-4 rounded-lg border border-purple-200 bg-purple-50/40 px-4 py-3 text-sm text-gray-700"
      }
    >
      {!compact && (
        <>
          <div className="font-medium text-gray-800">
            {/* Never numbered before confirm (D1). */}
            Reading the new upload · {file.filename}
          </div>
          <div className="text-xs text-gray-500" data-testid="upload-eta">
            {file.queue?.read
              ? file.queue.read.slow
                ? "Taking longer than usual. Large decks sometimes do."
                : `Ready to view in ${roughRange(file.queue.read.done_in)}.`
              : "Usually under a minute; longer plans take a little more."}{" "}
            You can leave this page — it keeps going, and you&apos;ll be
            notified when it&apos;s ready.
          </div>
        </>
      )}
      <ol
        className={`${compact ? "" : "mt-3 "}space-y-2 rounded-lg border border-gray-100 bg-white/70 p-3`}
        aria-label="Reading progress"
      >
        {rows.map(r => {
          const running = r.status === "active" ? elapsed(r.startedAt) : null;
          return (
            <li
              key={r.key}
              data-testid={`progress-${r.key}`}
              data-status={r.status}
              className="flex flex-wrap items-start gap-x-2"
            >
              {r.status === "done" ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                  aria-label="done"
                />
              ) : r.status === "active" ? (
                <Loader2
                  className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#974ae4]"
                  aria-label="in progress"
                />
              ) : r.status === "failed" ? (
                <XCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
                  aria-label="stopped here"
                />
              ) : (
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
                  aria-label="pending"
                />
              )}
              <span
                className={`flex-1 ${
                  r.status === "pending"
                    ? "text-gray-400"
                    : r.status === "active"
                      ? "font-medium text-purple-800"
                      : r.status === "failed"
                        ? "text-red-700"
                        : "text-gray-800"
                }`}
              >
                {r.label}
              </span>
              {r.status === "done" && r.ms != null && (
                <span className="font-mono text-xs text-gray-400">
                  {seconds(r.ms)}
                </span>
              )}
              {running != null && (
                <span className="font-mono text-xs text-purple-700">
                  {Math.floor(running / 1000)} s…
                </span>
              )}
              {r.items && <StepItems rowKey={r.key} items={r.items} />}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default UploadProgress;
